/**
 * Reading one Cataloging app diagnostic report for the Super Tenant Dash
 * (`SuperAppReports.tsx`). The app writes it (`apps/cataloging/src/diagnostics`)
 * and the server keeps it as sent, so every reader here takes `unknown` and
 * answers something sensible for a field that is missing or the wrong shape —
 * an older app version, a report cut short — rather than breaking the page.
 *
 * What each part answers, for the person reading it:
 *   hit tests        what a finger at each point would have landed on
 *   stuck gestures   any gesture left mid-gesture — the usual reason a screen
 *                    stops taking touches
 *   windows          anything covering the app (another window, a pop-up)
 *   recovery         what the watchdog reset, and whether touches came back
 *   events           what the worker did in the minutes before
 */

type Obj = Record<string, unknown>;

function obj(v: unknown): Obj {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : {};
}
function arr(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}
function str(v: unknown): string {
  return typeof v === 'string'
    ? v
    : typeof v === 'number' || typeof v === 'boolean'
      ? String(v)
      : '';
}
function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** "RCTViewComponentView (capture-focus-area)" — a view's class and its test id. */
export function describeView(entry: unknown): string {
  const e = obj(entry);
  const view = str(e.view) || str(e.class) || 'unknown view';
  const id = str(e.testID);
  return id ? `${view} (${id})` : view;
}

export interface HitTestRow {
  point: string;
  /** The view the touch would land on. */
  top: string;
  /** The first few views above it, nearest first. */
  above: string[];
  /** A view on the way up with touches switched off. */
  blocked: string | null;
}

export function readHitTests(report: unknown): HitTestRow[] {
  return arr(obj(obj(report).snapshot).hitTests).map((h) => {
    const hit = obj(h);
    const chain = arr(hit.chain);
    const off = chain.find((c) => obj(c).interaction === false);
    return {
      point: `${String(Math.round(num(hit.x) ?? 0))}, ${String(Math.round(num(hit.y) ?? 0))}`,
      top: chain.length ? describeView(chain[0]) : 'nothing',
      above: chain.slice(1, 5).map(describeView),
      blocked: off ? describeView(off) : null,
    };
  });
}

export interface GestureRow {
  name: string;
  state: string;
  touches: number;
  on: string;
}

/** Gestures that were not idle when the report was made. */
export function readStuckGestures(report: unknown): GestureRow[] {
  return arr(obj(obj(obj(report).snapshot).gestures).notIdle).map((g) => {
    const e = obj(g);
    return {
      name: str(e.class) || 'unknown',
      state: str(e.state) || 'unknown',
      touches: num(e.touches) ?? 0,
      on: describeView({ view: e.view, testID: e.testID }),
    };
  });
}

export interface WindowRow {
  name: string;
  level: number;
  /** "key", "hidden", "touches off", … — anything unusual. */
  flags: string[];
  presented: string[];
}

export function readWindows(report: unknown): WindowRow[] {
  return arr(obj(obj(obj(report).snapshot).health).windows).map((w) => {
    const e = obj(w);
    const flags: string[] = [];
    if (e.key === true) flags.push('key');
    if (e.hidden === true) flags.push('hidden');
    if (e.interaction === false) flags.push('touches off');
    const alpha = num(e.alpha);
    if (alpha !== null && alpha < 0.05) flags.push('invisible');
    return {
      name: str(e.class) || 'window',
      level: num(e.level) ?? 0,
      flags,
      presented: arr(e.presented).map(str).filter(Boolean),
    };
  });
}

/** Whether the app as a whole had touches switched off. */
export function readTouchesOff(report: unknown): boolean {
  const h = obj(obj(obj(report).snapshot).health);
  return h.ignoringInteractionEvents === true || h.keyWindowInteraction === false;
}

export interface RecoveryView {
  /** What the watchdog reset to free the screen. */
  reset: string[];
  /** "Touches came back" / "Still frozen" / "Nobody touched after" / null (no reset). */
  outcome: string | null;
}

export function readRecovery(report: unknown): RecoveryView {
  const r = obj(obj(report).recovery);
  if (!('reset' in r) && !('recovered' in r)) return { reset: [], outcome: null };
  const reset = arr(r.reset).map((g) => `${str(obj(g).class)} (${str(obj(g).state)})`);
  const outcome =
    r.recovered === true
      ? 'Touches came back'
      : r.recovered === false
        ? 'Still frozen'
        : 'Nobody touched the screen after';
  return { reset, outcome };
}

export interface EventRow {
  /** Seconds before the report was made, e.g. "−12.3 s". */
  when: string;
  type: string;
  data: string;
}

/** The app's recent events, oldest first, timed against the report. */
export function readEvents(report: unknown): EventRow[] {
  const r = obj(report);
  const madeAt = Date.parse(str(r.createdAt));
  return arr(r.events).map((ev) => {
    const e = obj(ev);
    const at = num(e.at);
    const when =
      at !== null && Number.isFinite(madeAt) ? `−${((madeAt - at) / 1000).toFixed(1)} s` : '';
    const data = Object.entries(obj(e.data))
      .map(([k, v]) => `${k}=${v === null ? 'null' : str(v)}`)
      .join(' ');
    return { when, type: str(e.type) || 'event', data };
  });
}

export interface TouchRow {
  when: string;
  point: string;
  on: string;
  /** False: the touch was on something React Native does not draw (a system alert). */
  inApp: boolean;
}

/** The last touches iOS delivered, oldest first. Times in the report are seconds. */
export function readRecentTouches(report: unknown): TouchRow[] {
  const r = obj(report);
  const madeAt = Date.parse(str(r.createdAt)) / 1000;
  return arr(obj(r.snapshot).recentTouches).map((t) => {
    const e = obj(t);
    const at = num(e.at);
    return {
      when: at !== null && Number.isFinite(madeAt) ? `−${(madeAt - at).toFixed(1)} s` : '',
      point: `${String(Math.round(num(e.x) ?? 0))}, ${String(Math.round(num(e.y) ?? 0))}`,
      on: describeView(e),
      inApp: e.inReact !== false,
    };
  });
}

export function readError(report: unknown): { message: string; stack: string } | null {
  const e = obj(obj(report).error);
  const message = str(e.message);
  return message ? { message, stack: str(e.stack) } : null;
}

/** The watchdog's numbers, worded. */
export function readDetail(report: unknown): string | null {
  const d = obj(obj(report).detail);
  const lost = num(d.lost);
  const over = num(d.overMs);
  const off = num(d.forMs);
  if (lost !== null && over !== null) {
    return `${String(lost)} touches reached the phone but not the app over ${(over / 1000).toFixed(1)} s`;
  }
  if (off !== null) return `Touches were switched off for ${(off / 1000).toFixed(1)} s`;
  return null;
}
